// src/views/whatsappInbox/ChatLeftMenuRedux.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { toast } from "react-hot-toast";
import { clearInputValueById } from "../../hooks/use-debounce";
import InboxListRedux from "./InboxListRedux";
import AddChat from "./AddChat";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchConversations,
  selectAllConversations,
  selectConversationsStatus,
  selectCurrentPage,
  selectFilters,
  selectSelectedConversation,
  selectTotalConversations,
  setCurrentPage,
  setFilter,
  setSelectedConversation,
  startNewChat,
  toggleFilter,
  User
} from "../../store/slices/conversationsSlice";
import { setShowAddChatModal, selectShowAddChatModal } from "../../store/slices/uiSlice";
import { Logger } from '../../utils/logger';
import debounce from "lodash/debounce";


interface ChatLeftMenuReduxProps {
    usersArray?: any[]; // For compatibility during migration
    selectedUser?: any;
    onConnect?: (user: any) => void; 
    listInnerRef?: React.RefObject<any>;
    filter?: any;
  }

  const ChatLeftMenuRedux: React.FC<ChatLeftMenuReduxProps> = ({
    usersArray,
    filter,
    selectedUser,
     onConnect, 
    
  }) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const filters = useAppSelector(selectFilters);
  const isLoading = useAppSelector(selectConversationsStatus) === 'loading';
  const showAddChatModal = useAppSelector(selectShowAddChatModal);
  const currentPage = useAppSelector(selectCurrentPage);
  const totalConversations = useAppSelector(selectTotalConversations);
  
  const defaultListRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const [searchValue, setSearchValue] = useState('');
  const debouncedFunctionRef = useRef<any>(null);

  // Local state (for compatibility with existing components)
  const [showAddChat, setShowAddChat] = useState(false);
  
  // Refs
  const listInnerRef = useRef<any>(null);

  // Logging for debugging
  Logger.log("ChatLeftMenuRedux rendered with conversations:", conversations?.length);

  // Add this inside your ChatLeftMenuRedux component
useEffect(() => {
    // Dispatch initial fetch when component mounts
    dispatch(fetchConversations({ page: 1, refresh: true }));
  }, [dispatch]);
  

// ✅ UPDATED: Better debounce setup with cleanup
useEffect(() => {
  debouncedFunctionRef.current = debounce((value: string) => {
    Logger.log("Setting search filter (debounced):", { key: 'search', value });
    // ✅ This will trigger the filter useEffect above
    dispatch(setFilter({ key: 'search' as any, value }));
  }, 500); // ✅ Increased debounce time for better performance
  
  return () => {
    // ✅ Enhanced cleanup
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
  };
}, [dispatch]);

// ✅ UPDATED: Better filter effect with initial load check
useEffect(() => {
  // Only fetch when filters change, not on initial render
  const hasFilters = Object.keys(filters).length > 0;
  const hasConversations = conversations && conversations.length > 0;
  
  if (hasFilters || hasConversations) {
    Logger.log("Filters changed, fetching conversations with RESET pagination", filters);
    // ✅ CRITICAL: Always reset to page 1 when filters change
    dispatch(fetchConversations({ page: 1, refresh: true }));
  }
}, [filters]); // ✅ Remove dispatch dependency to prevent loops

// ✅ UPDATED: Improved handleFilter with proper debouncing
const handleFilter = (input: any) => {
  const { name, value } = input.target;
  
  if (name === 'search') {
    // ✅ Update local state immediately for UI responsiveness
    setSearchValue(value);
    
    // ✅ Cancel previous debounced call
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
    
    // ✅ Trigger debounced search
    debouncedFunctionRef.current(value);
  } else {
    // ✅ Non-search filters trigger immediate action
    dispatch(setFilter({ key: name as any, value }));
  }
};

  const handleTagFilter = (filterKey: string) => {
    dispatch(toggleFilter(filterKey as any));
    Logger.log("Toggling filter:", filterKey);
  };

  const handleConnect = (user: User) => {
    dispatch(setSelectedConversation(user));
    Logger.log("Setting selected conversation:", user.id);
  };


  
  const handleStartChat = async (data: { phone: string; name?: string }) => {
    try {
      Logger.log("Starting new chat with:", data);
      
      await dispatch(startNewChat({ 
        phone: data.phone,
        message: "Hello!" 
      })).unwrap();
      
      // Refresh conversations after starting a new chat
      dispatch(fetchConversations({ page: 1, refresh: true }));
      
      toast.success("Chat started successfully");
      dispatch(setShowAddChatModal(false));
    } catch (err) {
      console.error("Failed to start chat:", err);
      toast.error("Failed to start chat");
    }
  };

  // Infinite scrolling
  const onScrollListing = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      
      if (scrollTop + clientHeight >= scrollHeight - 1 && !isLoading) {
        const totalConvo = conversations?.reduce(
          (acc, group) => acc + group.data.length, 0
        ) || 0;
        
        if (totalConvo < totalConversations) {
          const nextPage = currentPage + 1;
          Logger.log("Loading more conversations, page:", nextPage);
          dispatch(setCurrentPage(nextPage));
          dispatch(fetchConversations({ page: nextPage }));
        }
      }
    }
  };

  return (
    <div className="userLists pe-1">
      <div className="chat-list-wrapper">
        {/* New Chat Button */}
        <div style={{ 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center',
  marginBottom: '4px',
  padding: '0 12px'
}}>
  <span style={{ 
    fontSize: '20px', 
    fontWeight: 600,
    color: '#333'
  }}>
    Inbox ({totalConversations})
  </span>
  <Button
  onClick={() => dispatch(setShowAddChatModal(true))}
  type="primary"
  style={{ 
    minWidth: '100px', 
    borderRadius: '8px',
    fontWeight: 500,
    height: '32px',
    marginTop: '8px' // Adjust this value as needed
  }}
>
  New Chat
</Button>

</div>



        <AddChat
          visible={showAddChatModal}
          onClose={() => dispatch(setShowAddChatModal(false))}
          onStartChat={handleStartChat}
        />

        {/* Search & Filter Card */}
        <div className="search-filter-card">
          <div className="search-wrapper">
            <i className="fa-sharp fa-magnifying-glass search-icon"></i>
            <input
  ref={inputRef}
  className="search-input"
  placeholder="Type to search..."
  name="search"
  id="searchInput"
  onChange={handleFilter}
  value={searchValue}
  type="text"
/>
{searchValue && (
  <button
    className="clear-btn"
    onClick={() => {
      // ✅ UPDATED: Proper clear logic with debounce cancellation
      
      // 1. Cancel any pending debounced search
      if (debouncedFunctionRef.current?.cancel) {
        debouncedFunctionRef.current.cancel();
      }
      
      // 2. Clear local state immediately for UI feedback
      setSearchValue("");
      
      // 3. Clear Redux state immediately (no debounce for clear)
      dispatch(setFilter({ key: 'search' as any, value: "" }));
      
      // 4. Clear input element (backup)
      clearInputValueById("searchInput");
    }}
  >
    <i className="fa-regular fa-times"></i>
  </button>
)}
          </div>

          {/* Filter Tags */}
          <div className="filter-tags-row">
            {/* Automations filter button */}
            <button
              className={`filter-tag ${filters.isAutomation ? "active" : ""}`}
              onClick={() => handleTagFilter("isAutomation")}
            >
              <i className="fa-regular fa-robot"></i>
              Automations
              {filters.isAutomation && <i className="fa-regular fa-xmark"></i>}
            </button>

            <button
              className={`filter-tag ${filters.isRead === false ? "active" : ""}`}
              onClick={() => handleTagFilter("isRead")}
            >
              <i className="fa-regular fa-envelope"></i>
              Unread
              {filters.isRead === false && <i className="fa-regular fa-xmark"></i>}
            </button>

            <button
              className={`filter-tag ${filters.isAssigned === false ? "active" : ""}`}
              onClick={() => handleTagFilter("isAssigned")}
            >
              <i className="fa-regular fa-user"></i>
              Unassigned
              {filters.isAssigned === false && <i className="fa-regular fa-xmark"></i>}
            </button>

            <button
              className={`filter-tag ${filters.isAssignedToMe ? "active" : ""}`}
              onClick={() => handleTagFilter("isAssignedToMe")}
            >
              <i className="fa-regular fa-user-check"></i>
              Assigned to me
              {filters.isAssignedToMe && <i className="fa-regular fa-xmark"></i>}
            </button>
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-fill position-relative">
          <div className="userListItems">
            {conversations?.length ? (
              <ul
                className="chat_list_container"
                ref={listInnerRef}
                onScroll={onScrollListing}
              >
            <InboxListRedux 
  onConnect={onConnect} // ← NOW USE THE DESTRUCTURED PROP
/>
              </ul>
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center">
                <h5 className="text-dark fm-gilroy fs-6 text-center">
                  {isLoading ? "Loading..." : "No results found"}
                </h5>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatLeftMenuRedux;